import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import css from './PredictionModal.module.css';
import { IconClose } from '../../components';
import SearchIcon from '../Topbar/SearchIcon';
import LocationPredictionsList from './LocationPredictionsList';
import { GeocoderAttribution } from '../LocationAutocompleteInputOld/GeocoderGoogleMaps';
import { useHistory } from 'react-router-dom';

const PredictionModal = ({
  lang,
  onChange,
  renderPredictions,
  predictions,
  predictionsLoading,
  currentLocationId,
  isGoogleMapsInUse,
  geocoder,
  highlightedIndex,
  onSelectStart,
  onSelectMove,
  onSelectEnd,
  search,
  predictionsAttributionClassName,
  rootClassName,
  section,
}) => {
  const history = useHistory();
  const inputRef = useRef(null);

  const [openModal, setOpenModal] = useState(renderPredictions);
  const searchString = typeof search === 'string' ? search : '';
  const [searchKey, setSearchKey] = useState(searchString || '');
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const handleSearch = e => {
    setSearchKey(e.target.value);
    onChange(e);
  };

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Update searchKey when search prop changes
  useEffect(() => {
    const searchString = typeof search === 'string' ? search : '';
    setSearchKey(searchString || '');
  }, [search]);

  useEffect(() => {
    if (renderPredictions) {
      setOpenModal(true && isSmallScreen);

      if (isSmallScreen) {
        const currentUrl = new URL(window.location.href);
        const params = new URLSearchParams(currentUrl.search);
        params.set('navbarHidden', 'true');
        params.set('section', section);

        const newSearch = params.toString();
        const currentSearch = currentUrl.search.startsWith('?')
          ? currentUrl.search.slice(1)
          : currentUrl.search;

        if (newSearch !== currentSearch) {
          history.replace({
            pathname: currentUrl.pathname,
            search: newSearch,
          });
        }
      }
    }
  }, [renderPredictions, isSmallScreen, history, section]);

  // Lock body scroll when modal is open.
  // The landing page scrolls via document.body (not window), so we save/restore
  // document.body.scrollTop and use overflow:hidden instead of position:fixed to
  // avoid wiping the body's scroll position on iOS/mobile browsers.
  // IMPORTANT: only restore scroll when we are transitioning from an actually
  // locked state. Otherwise mounting this component (e.g. when the mobile
  // navbar search bar is conditionally rendered while scrolling) would force
  // the page back to the top.
  const scrollYRef = useRef(0);
  const wasLockedRef = useRef(false);
  useEffect(() => {
    const restoreScroll = () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.scrollTop = scrollYRef.current;
      window.scrollTo(0, scrollYRef.current);
    };

    if (openModal) {
      scrollYRef.current = document.body.scrollTop || window.scrollY || 0;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      wasLockedRef.current = true;
    } else if (wasLockedRef.current) {
      restoreScroll();
      wasLockedRef.current = false;
    }

    return () => {
      if (wasLockedRef.current) {
        restoreScroll();
        wasLockedRef.current = false;
      }
    };
  }, [openModal]);

  useEffect(() => {
    if (openModal && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, [openModal]);

  const handleClose = () => {
    setOpenModal(false);

    if (isSmallScreen) {
      const currentUrl = new URL(window.location.href);
      const params = new URLSearchParams(currentUrl.search);

      params.delete('navbarHidden');
      params.delete('section');

      const newSearch = params.toString();
      const currentSearch = currentUrl.search.startsWith('?')
        ? currentUrl.search.slice(1)
        : currentUrl.search;

      if (newSearch !== currentSearch) {
        history.replace({
          pathname: currentUrl.pathname,
          search: newSearch,
        });
      }
    }
  };

  if (!openModal) return null;

  // Render into <body> via a portal. The mobile navbar search slot is
  // position:absolute with a low z-index, which creates a stacking context that
  // would otherwise trap this fixed overlay *behind* the hero/category/results
  // (they paint above it). Portaling to body lets the overlay cover the page.
  const modal = (
    <div className={css.modalOverlay}>
      <div className={css.modalContent}>
        <div className={css.modalHeader}>
          <button className={css.closeButton} onClick={handleClose}>
            <IconClose />
          </button>
          <div className={css.searchBar}>
            <input
              ref={inputRef}
              type="text"
              placeholder={lang === 'de' ? 'Reiseziel suchen...' : 'Search destination...'}
              value={searchKey}
              onChange={e => handleSearch(e)}
              className={css.searchInput}
            />
            <SearchIcon className={css.searchEndIcon} />
          </div>
        </div>
        <div className={css.modalBody}>
          <LocationPredictionsList
            rootClassName={rootClassName}
            predictions={predictions}
            predictionsLoading={predictionsLoading}
            currentLocationId={currentLocationId}
            isGoogleMapsInUse={isGoogleMapsInUse}
            geocoder={geocoder}
            highlightedIndex={highlightedIndex}
            onSelectStart={onSelectStart}
            onSelectMove={onSelectMove}
            onSelectEnd={onSelectEnd}
            handleClose={handleClose}
            lang={lang}
            search={search}
          >
            <GeocoderAttribution className={predictionsAttributionClassName} />
          </LocationPredictionsList>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? ReactDOM.createPortal(modal, document.body) : modal;
};

export default PredictionModal;
